import { useState } from 'react';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate a successful submission (replace with real API call if needed)
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-emerald-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 mb-10 sm:mb-16">
          <div>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Get in Touch</h3>
            <p className="text-emerald-100 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
              Ready to start your study abroad journey? Contact us today for a free consultation.
              Our team is here to answer all your questions and guide you every step of the way.
            </p>

            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-200 mb-2">Contact Us</p>
                <p className="text-base">123 Education Plaza, Downtown District</p>
                <p className="text-base">City, Country</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-200 mb-2">Phone</p>
                <p className="text-base">+1 (555) 123-4567</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-200 mb-2">Email</p>
                <p className="text-base">hello@greenify-edu.com</p>
              </div>

              <div className="flex gap-3 sm:gap-4 pt-4 flex-wrap">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-emerald-600 transition-all cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-emerald-600 transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-emerald-600 transition-all cursor-pointer"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a
                  href="https://wa.me/15551234567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-emerald-600 transition-all cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8">
            <h4 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h4>
            {/* The data-readdy-form attribute now has an explicit empty string value to satisfy JSX parsing */}
            <form
              id="greenify-contact-form"
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-slate-90
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="Tell us about your study abroad goals..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value.slice(0, 500) })
                  }
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-emerald-600 text-sm text-center">
                  Message sent successfully! We'll get back to you soon.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm text-center">
                  Failed to send message. Please try again.
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-emerald-500 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-emerald-100">
              © 2025 Greenify Educational Consult. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
              <a href="#" className="text-sm text-emerald-100 hover:text-white transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-emerald-100 hover:text-white transition-colors cursor-pointer">
                Terms of Service
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-emerald-100 hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
